import React from "react";
import { MapPin } from "lucide-react";

/**
 * Decorative trail map with location markers.
 * Coordinates are 0–100 percentage values.
 */
export default function TrailMap({ stops = [], image, accent = "#C06E52" }) {
  return (
    <div
      data-testid="trail-map"
      className="relative w-full aspect-[4/3] md:aspect-[16/10] border border-brand-line/70 overflow-hidden"
    >
      {/* Topographic background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-95"
        style={{
          backgroundImage: image
            ? `url(${image})`
            : "linear-gradient(135deg,#EBE3D5 0%,#D9C5B2 100%)",
        }}
      />
      <div className="absolute inset-0 bg-brand-cream/60 backdrop-blur-[2px]" />

      {/* Contour lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {[...Array(8)].map((_, i) => (
          <path
            key={i}
            d={`M0 ${20 + i * 9} Q 25 ${10 + i * 9} 50 ${22 + i * 9} T 100 ${18 + i * 9}`}
            stroke="#2C4C3B"
            strokeWidth="0.18"
            fill="none"
            opacity="0.35"
          />
        ))}
        {/* Trail line connecting stops */}
        {stops.length > 1 && (
          <path
            d={
              "M " +
              stops
                .map((s) => `${s.coords[0]} ${s.coords[1]}`)
                .join(" L ")
            }
            stroke={accent}
            strokeWidth="0.55"
            strokeLinecap="round"
            strokeDasharray="1.2 1.4"
            fill="none"
          />
        )}
      </svg>

      {/* Markers */}
      {stops.map((s) => (
        <div
          key={s.n}
          className="absolute -translate-x-1/2 -translate-y-1/2 group"
          style={{ left: `${s.coords[0]}%`, top: `${s.coords[1]}%` }}
          data-testid={`map-marker-${s.n}`}
        >
          <span className="absolute -inset-3 rounded-full bg-brand-ochre/30 animate-pulse-dot" />
          <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-brand-cream border border-brand-ochre text-brand-ochre">
            <MapPin size={12} />
          </span>
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity bg-brand-charcoal text-brand-cream text-[10px] uppercase tracking-wider px-2 py-1 whitespace-nowrap">
            {String(s.n).padStart(2, "0")} · {s.title}
          </div>
        </div>
      ))}

      {/* Compass */}
      <div className="absolute top-4 right-4 w-10 h-10 border border-brand-charcoal/40 rounded-full flex items-center justify-center text-[10px] tracking-widest text-brand-charcoal/70 bg-brand-cream/80">
        N
      </div>
    </div>
  );
}
